/**
 * weatherApi.ts — per-point daily forecast for the trip planner.
 *
 * Wraps GET /api/weather (Open-Meteo, no key). Public + cached server-side,
 * so we also memoise per rounded coordinate in-process to avoid re-fetching
 * the same night's anchor.
 */

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

export interface DayForecast {
  date: string;              // YYYY-MM-DD
  condition: string;
  temp_max_c: number | null;
  temp_min_c: number | null;
  rain_chance_pct: number | null;
  rain_mm: number | null;
  wind_max_kmh: number | null;
  uv_index_max: number | null;
}

export interface WeatherResult {
  forecast_days: number;
  daily_forecast: DayForecast[];
}

const _cache = new Map<string, Promise<WeatherResult | null>>();

export function fetchWeather(lat: number, lng: number): Promise<WeatherResult | null> {
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
  const hit = _cache.get(key);
  if (hit) return hit;
  const p = (async (): Promise<WeatherResult | null> => {
    try {
      const res = await fetch(`${API_BASE}/weather?lat=${lat}&lng=${lng}`);
      if (!res.ok) return null;
      const data = await res.json();
      if (!Array.isArray(data?.daily_forecast)) return null;
      return data as WeatherResult;
    } catch {
      return null;
    }
  })();
  _cache.set(key, p);
  return p;
}

/** Pick the forecast for a specific date, if it's within the 14-day window. */
export function forecastForDate(
  w: WeatherResult | null,
  date: string | undefined
): DayForecast | null {
  if (!w || !date) return null;
  return w.daily_forecast.find((d) => d.date === date) || null;
}

/** A weather emoji for an Open-Meteo condition string (from _WMO_WEATHER_CODES). */
export function weatherEmoji(condition: string): string {
  const c = (condition || '').toLowerCase();
  if (c.includes('thunder')) return '⛈️';
  if (c.includes('snow')) return '🌨️';
  if (c.includes('rain') || c.includes('drizzle') || c.includes('shower')) return '🌧️';
  if (c.includes('fog')) return '🌫️';
  if (c.includes('overcast')) return '☁️';
  if (c.includes('cloud')) return '⛅';
  if (c.includes('clear') || c.includes('sunny')) return '☀️';
  return '🌤️';
}
